import React, { useState } from "react";
import { FiGlobe, FiSmartphone, FiCreditCard, FiMonitor, FiPenTool, FiZap, FiCode, FiServer, FiDatabase, FiCloud, FiTerminal, FiArrowUpRight, FiPhoneCall, FiCheckCircle, FiPlus, FiMinus } from "react-icons/fi";
import "./Home.css";
import ApplicationForm from "./ApplicationForm";
import { triggerApplyModal } from "../App";

const Home = () => {
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // FAQ Accordion State
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const techServices = [
    {
      id: "web-dev",
      icon: <FiGlobe />,
      badge: "Web Engineering",
      title: "Custom Web Application Development",
      desc: "High-speed, SEO-optimized web portals, client dashboards, e-commerce platforms, and admin controls built with React, Next.js, and Node.js.",
      benefits: ["Sub-second page loading speed", "Responsive across all screen sizes", "Robust security & admin permissions"]
    },
    {
      id: "app-dev",
      icon: <FiSmartphone />,
      badge: "Mobile Engineering",
      title: "iOS & Android Mobile App Dev",
      desc: "Cross-platform and native mobile apps created with Flutter and React Native. Complete with offline mode, biometric login, and push alerts.",
      benefits: ["Biometric & OTP authentication", "Seamless Payment Gateway integration", "App Store & Play Store publishing"]
    },
    {
      id: "fintech-software",
      icon: <FiCreditCard />,
      badge: "Fintech Systems",
      title: "Custom Fintech Software & Portals",
      desc: "Enterprise loan origination software, automated e-KYC workflows, repayment gateways, and NBFC management portals built to strict compliance standards.",
      benefits: ["Automated CIBIL & e-KYC integration", "256-bit bank-grade encryption", "Real-time loan ledger & reporting"]
    },
    {
      id: "saas-cloud",
      icon: <FiMonitor />,
      badge: "SaaS & Cloud",
      title: "Custom SaaS & Cloud Architecture",
      desc: "Scalable cloud backends, microservices, GraphQL/REST APIs, and multi-tenant SaaS platforms engineered for high throughput on AWS/GCP.",
      benefits: ["Zero-downtime deployment pipelines", "Auto-scaling cloud infrastructure", "Microservices architecture"]
    },
    {
      id: "ui-ux",
      icon: <FiPenTool />,
      badge: "Product Design",
      title: "UI/UX & Product Prototype Design",
      desc: "User-centric UI/UX design, interactive Figma prototypes, brand design systems, and wireframes tailored for maximum conversion.",
      benefits: ["Interactive Figma clickable mockups", "Modern design system library", "Conversion-focused user journeys"]
    },
    {
      id: "api-integration",
      icon: <FiZap />,
      badge: "APIs & DevOps",
      title: "API Development & System Integration",
      desc: "Custom REST/GraphQL APIs, webhook receivers, third-party ERP integrations, payment gateways (Razorpay, Cashfree, Stripe), and CI/CD setup.",
      benefits: ["High-speed API latency (<50ms)", "Comprehensive Swagger documentation", "Secure webhook callback handling"]
    }
  ];

  const techStack = [
    { name: "React / Next.js", category: "Frontend", icon: <FiCode />, desc: "Modern SSR/SSG web portals, atomic design systems, and fast client interactivity." },
    { name: "Node.js / Express", category: "Backend", icon: <FiServer />, desc: "High-throughput REST & GraphQL APIs, microservices, and serverless logic." },
    { name: "Flutter / React Native", category: "Mobile", icon: <FiSmartphone />, desc: "Cross-platform iOS & Android apps with offline caching, push alerts, and biometrics." },
    { name: "PostgreSQL / MongoDB", category: "Database", icon: <FiDatabase />, desc: "ACID-compliant relational schemas, document databases, indexing, and clustering." },
    { name: "AWS / Docker / GCP", category: "Cloud & DevOps", icon: <FiCloud />, desc: "Containerized deployments, CI/CD pipelines, and cloud auto-scaling infrastructure." },
    { name: "TypeScript / Python", category: "Languages", icon: <FiTerminal />, desc: "Type-safe enterprise software engines, data automation, and AI workflows." }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Discovery & Scope",
      desc: "We analyze your business goals, target audience, and functional requirements to draft a detailed technical specification."
    },
    {
      step: "02",
      title: "UI/UX & Architecture",
      desc: "Our design team crafts intuitive wireframes and UI designs while architects map out the database schema and system APIs."
    },
    {
      step: "03",
      title: "Agile Development",
      desc: "We build your software in bi-weekly sprints with continuous demo feedback, clean code reviews, and automated testing."
    },
    {
      step: "04",
      title: "QA & Deployment",
      desc: "Thorough security testing, performance tuning, and cloud deployment followed by 24/7 post-launch maintenance."
    }
  ];

  const FAQs = [
    {
      q: "What tech stack does Kubersetu Infotech specialize in?",
      a: "Kubersetu Infotech Private Limited specializes in modern full-stack web and mobile development using React, Next.js, Node.js, TypeScript, Flutter, React Native, PostgreSQL, MongoDB, and cloud infrastructure on AWS and GCP."
    },
    {
      q: "How long does a typical custom software project take?",
      a: "Simple web applications or mobile app MVPs typically take 3 to 5 weeks. Complex custom SaaS platforms or full fintech software portals take between 6 to 12 weeks depending on feature requirements."
    },
    {
      q: "Who owns the source code and IP after completion?",
      a: "You retain 100% full intellectual property (IP) and full source code ownership upon final project completion and payment."
    },
    {
      q: "Do you provide post-launch maintenance and support?",
      a: "Yes! We offer dedicated maintenance packages, server uptime monitoring, bug fixes, feature upgrades, and 24/7 technical support."
    },
    {
      q: "How do I get a free quote for my software project?",
      a: "Click on 'Request Tech Consultation' or fill out our project inquiry form with your requirements. Our technical solution team will schedule a free brief and share a detailed proposal within 24 hours."
    }
  ];

  const testimonials = [
    {
      id: 1,
      text: "Kubersetu Infotech engineered our full web application and admin portal in under 4 weeks. The page load speeds are blazingly fast and code quality is outstanding!",
      author: "Vikram Mehta",
      role: "CTO, FinTech Startup"
    },
    {
      id: 2,
      text: "Their mobile app engineering team built a smooth Flutter app for both iOS and Android with integrated OTP and biometric authentication. Highly professional developers!",
      author: "Ananya Roy",
      role: "Product Lead, Retail Platform"
    },
    {
      id: 3,
      text: "Working with Kubersetu Infotech was effortless. Clear sprint updates, excellent UI design, and spotless cloud deployment on AWS.",
      author: "Rohan Verma",
      role: "Founder, SaaS Innovations"
    }
  ];

  return (
    <div className="fintech-home">
      {/* ===== HERO SECTION ===== */}
      <section className="fintech-hero">
        <div className="hero-background-shapes">
          <div className="glow-orb orb-1"></div>
          <div className="glow-orb orb-2"></div>
        </div>

        <div className="fintech-container hero-grid">
          {/* Left Hero Content */}
          <div className="hero-text-content">
            <span className="kubersetu-badge">
              KUBERSETU INFOTECH PRIVATE LIMITED
            </span>

            <h1 className="hero-main-title">
              Engineering High-Performance
              <span className="gradient-text"> Web, Mobile & Software </span>
              Solutions
            </h1>

            <p className="hero-description">
              <strong>Kubersetu Infotech</strong> delivers end-to-end custom web application engineering, native iOS/Android mobile apps, fintech software platforms, and cloud solutions built for extreme performance and scale.
            </p>

            <div className="hero-cta-group">
              <button
                className="btn btn-primary hero-btn"
                onClick={() => triggerApplyModal('Custom Web Application Development')}
              >
                Request Tech Consultation <FiArrowUpRight />
              </button>
              <a href="/services" className="btn btn-secondary hero-btn-secondary">
                Explore All Services <FiMonitor />
              </a>
            </div>

            {/* Quick Trust Badges */}
            <div className="hero-trust-badges">
              <div className="trust-item">
                <span className="trust-number">120+</span>
                <span className="trust-label">Projects Delivered</span>
              </div>
              <div className="trust-divider"></div>
              <div className="trust-item">
                <span className="trust-number">99.8%</span>
                <span className="trust-label">On-Time Delivery</span>
              </div>
              <div className="trust-divider"></div>
              <div className="trust-item">
                <span className="trust-number">100%</span>
                <span className="trust-label">Code Ownership</span>
              </div>
            </div>
          </div>

          {/* Right Hero - 3D Floating Code Editor */}
          <style>{`
            .hero-3d-wrapper {
              perspective: 1200px;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 500px;
              position: relative;
            }
            .code-editor-3d {
              width: 100%;
              max-width: 550px;
              background: rgba(13, 17, 23, 0.85);
              backdrop-filter: blur(20px);
              border: 1px solid rgba(255, 255, 255, 0.15);
              border-radius: 16px;
              box-shadow: 
                -20px 30px 60px rgba(0,0,0,0.6), 
                inset 0 1px 0 rgba(255,255,255,0.2),
                0 0 40px rgba(59, 130, 246, 0.2);
              display: flex;
              flex-direction: column;
              transform: rotateY(-12deg) rotateX(8deg) rotateZ(-2deg);
              animation: float3D 6s ease-in-out infinite;
              position: relative;
              z-index: 10;
              transform-style: preserve-3d;
            }
            .glow-behind-editor {
              position: absolute;
              width: 300px;
              height: 300px;
              background: radial-gradient(circle, rgba(59,130,246,0.3) 0%, rgba(0,0,0,0) 70%);
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              border-radius: 50%;
              filter: blur(40px);
              z-index: 0;
              animation: pulseGlow 4s infinite alternate;
            }
            .glow-behind-editor-2 {
              position: absolute;
              width: 250px;
              height: 250px;
              background: radial-gradient(circle, rgba(16,185,129,0.2) 0%, rgba(0,0,0,0) 70%);
              top: 40%;
              right: 10%;
              border-radius: 50%;
              filter: blur(40px);
              z-index: 0;
              animation: pulseGlow 5s infinite alternate-reverse;
            }
            @keyframes float3D {
              0% { transform: rotateY(-12deg) rotateX(8deg) rotateZ(-2deg) translateY(0px); }
              50% { transform: rotateY(-8deg) rotateX(12deg) rotateZ(-1deg) translateY(-20px); }
              100% { transform: rotateY(-12deg) rotateX(8deg) rotateZ(-2deg) translateY(0px); }
            }
            @keyframes pulseGlow {
              0% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
              100% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
            }
          `}</style>

          <div className="hero-3d-wrapper">
            <div className="glow-behind-editor"></div>
            <div className="glow-behind-editor-2"></div>

            <div className="code-editor-3d">
              {/* Editor Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
                background: 'rgba(22, 27, 34, 0.7)',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px'
              }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ff5f56', boxShadow: '0 0 10px #ff5f56' }}></div>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ffbd2e', boxShadow: '0 0 10px #ffbd2e' }}></div>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#27c93f', boxShadow: '0 0 10px #27c93f' }}></div>
                </div>
                <div style={{ margin: '0 auto', color: '#8b949e', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                  cloud_engine.ts
                </div>
              </div>

              {/* Editor Body */}
              <div style={{
                padding: '24px',
                fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                fontSize: '0.9rem',
                lineHeight: '1.7',
                color: '#c9d1d9',
                overflowX: 'hidden'
              }}>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>1</span>
                  <span><span style={{ color: '#ff7b72' }}>import</span> {'{'} <span style={{ color: '#79c0ff' }}>Serverless</span>, <span style={{ color: '#79c0ff' }}>AI</span> {'}'} <span style={{ color: '#ff7b72' }}>from</span> <span style={{ color: '#a5d6ff' }}>'@kubersetu/cloud'</span>;</span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>2</span>
                  <span></span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>3</span>
                  <span><span style={{ color: '#8b949e' }}>{"// Initialize High-Performance Infrastructure"}</span></span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>4</span>
                  <span><span style={{ color: '#ff7b72' }}>const</span> <span style={{ color: '#79c0ff' }}>engine</span> <span style={{ color: '#ff7b72' }}>=</span> <span style={{ color: '#ff7b72' }}>new</span> <span style={{ color: '#d2a8ff' }}>Serverless</span>({'{'}</span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>5</span>
                  <span>&nbsp;&nbsp;architecture: <span style={{ color: '#a5d6ff' }}>'Microservices'</span>,</span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>6</span>
                  <span>&nbsp;&nbsp;speed: <span style={{ color: '#a5d6ff' }}>'Ultra-Low Latency'</span>,</span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>7</span>
                  <span>&nbsp;&nbsp;ai_integration: <span style={{ color: '#79c0ff' }}>true</span></span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>8</span>
                  <span>{'}'});</span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>9</span>
                  <span></span>
                </div>
                <div style={{ display: 'flex' }}>
                  <span style={{ color: '#8b949e', userSelect: 'none', marginRight: '16px' }}>10</span>
                  <span><span style={{ color: '#ff7b72' }}>await</span> <span style={{ color: '#79c0ff' }}>engine</span>.<span style={{ color: '#d2a8ff' }}>deployToGlobalEdge</span>();</span>
                </div>
                <div style={{ display: 'flex', marginTop: '20px' }}>
                  <span style={{ color: '#79c0ff', animation: 'pulse 1.5s infinite', textShadow: '0 0 10px rgba(121, 192, 255, 0.5)' }}>&gt; Systems Online. Traffic routed 🚀</span>
                </div>
              </div>

              {/* Floating Status Badge */}
              <div style={{
                position: 'absolute',
                bottom: '-20px',
                right: '-30px',
                background: 'rgba(16, 185, 129, 0.2)',
                backdropFilter: 'blur(16px)',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                color: '#10b981',
                fontWeight: '700',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 15px 35px rgba(0,0,0,0.4), 0 0 20px rgba(16,185,129,0.2)',
                transform: 'rotate(-5deg) translateZ(30px)',
                zIndex: 20
              }}>
                <FiCheckCircle size={20} /> Zero Downtime
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CORE SERVICES GRID ===== */}
      <section className="fintech-products-section" id="services">
        <div className="fintech-container">
          <div className="section-header-center">
            <span className="section-eyebrow">OUR CORE SOFTWARE CAPABILITIES</span>
            <h2>Technology & Software Development Services</h2>
            <p>At Kubersetu Infotech Private Limited, we turn complex technical requirements into elegant, high-performing digital products.</p>
          </div>

          <div className="products-grid">
            {techServices.map((prod) => (
              <div key={prod.id} className="bento-box product-card">
                <div className="product-top-row">
                  <span className="product-icon">{prod.icon}</span>
                  <span className="product-badge">{prod.badge}</span>
                </div>
                <h3 className="product-title">{prod.title}</h3>
                <p className="product-desc">{prod.desc}</p>

                <ul className="product-checklist">
                  {prod.benefits.map((b, i) => (
                    <li key={i}>
                      <span className="check-icon"><FiCheckCircle /></span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className="btn btn-secondary w-full product-apply-btn"
                  onClick={() => triggerApplyModal(prod.title)}
                >
                  Request Consultation <FiArrowUpRight />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TECH STACK SHOWCASE ===== */}
      <section className="fintech-eligibility-section" style={{ background: 'rgba(15, 23, 42, 0.6)' }}>
        <div className="fintech-container">
          <div className="section-header-center">
            <span className="section-eyebrow">CUTTING-EDGE STACK</span>
            <h2>Frameworks & Tools We Excel In</h2>
            <p>We build with modern, production-tested technologies for maximum speed, security, and scalability.</p>
          </div>

          <div className="tech-stack-grid">
            {techStack.map((item, index) => (
              <div key={index} className="bento-box tech-stack-card">
                <div className="tech-card-header">
                  <span className="tech-card-icon">{item.icon}</span>
                  <span className="tech-card-category">{item.category}</span>
                </div>
                <h3 className="tech-card-name">{item.name}</h3>
                <p className="tech-card-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW WE WORK (4-STEP PROCESS) ===== */}
      <section className="fintech-process-section" id="how-it-works">
        <div className="fintech-container">
          <div className="section-header-center">
            <span className="section-eyebrow">OUR METHODOLOGY</span>
            <h2>Agile 4-Step Engineering Sprint</h2>
            <p>From initial concept brief to production deployment, we deliver seamlessly on schedule.</p>
          </div>

          <div className="process-grid">
            {processSteps.map((step, idx) => (
              <div key={idx} className="bento-box process-card">
                <div className="step-badge">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS SECTION ===== */}
      <section className="fintech-testimonials-section">
        <div className="fintech-container">
          <div className="section-header-center">
            <span className="section-eyebrow">CLIENT FEEDBACK</span>
            <h2>What Founders & CTOs Say</h2>
          </div>

          <div className="bento-box testimonial-box">
            <div className="quote-mark">“</div>
            <p className="testimonial-text">
              "{testimonials[activeTestimonial].text}"
            </p>
            <div className="testimonial-author-row">
              <div className="author-avatar">
                {testimonials[activeTestimonial].author.charAt(0)}
              </div>
              <div className="testimonial-author-info">
                <h4>{testimonials[activeTestimonial].author}</h4>
                <p>{testimonials[activeTestimonial].role}</p>
              </div>
            </div>

            <div className="testimonial-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`dot ${i === activeTestimonial ? "active" : ""}`}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Go to slide ${i + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ SECTION ===== */}
      <section className="fintech-faq-section">
        <div className="fintech-container">
          <div className="section-header-center">
            <span className="section-eyebrow">FREQUENTLY ASKED QUESTIONS</span>
            <h2>Got Questions About Our Tech Services?</h2>
          </div>

          <div className="faq-accordion">
            {FAQs.map((faq, index) => (
              <div
                key={index}
                className={`bento-box faq-item ${openFaqIndex === index ? "open" : ""}`}
                onClick={() => setOpenFaqIndex(openFaqIndex === index ? -1 : index)}
              >
                <div className="faq-question">
                  <h3>{faq.q}</h3>
                  <span className="faq-toggle">{openFaqIndex === index ? <FiMinus /> : <FiPlus />}</span>
                </div>
                {openFaqIndex === index && (
                  <div className="faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="fintech-cta-banner">
        <div className="fintech-container">
          <div className="cta-inner bento-box">
            <span className="section-eyebrow cta-eyebrow"><FiZap /> LET'S BUILD TOGETHER</span>
            <h2>Ready to Build Your Next Web or Mobile Application?</h2>
            <p className="cta-subtitle">
              Partner with <strong>Kubersetu Infotech Private Limited</strong> for reliable, high-performance tech engineering.
            </p>

            <div className="cta-trust-badges">
              <span><FiCheckCircle /> Rapid Delivery</span>
              <span><FiCheckCircle /> NDA & IP Security</span>
              <span><FiCheckCircle /> 100% Code Ownership</span>
              <span><FiCheckCircle /> Dedicated Lead Engineer</span>
            </div>

            <div className="cta-actions">
              <button
                className="btn btn-primary cta-btn-primary"
                onClick={() => triggerApplyModal('Custom Web Application Development')}
              >
                Request Free Consultation <FiArrowUpRight />
              </button>
              <button
                className="btn btn-secondary cta-btn-secondary"
                onClick={() => triggerApplyModal('Custom Web Application Development')}
              >
                Schedule Tech Call <FiPhoneCall />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== APPLICATION MODAL ===== */}
      {showApplicationForm && (
        <ApplicationForm onClose={() => setShowApplicationForm(false)} />
      )}
    </div>
  );
};

export default Home;
